import type { FeatureFlagKey } from './flags';
import { featureFlagDefinitions, featureFlagKeys } from './flags';

export type FeatureFlagValues = Partial<Record<FeatureFlagKey, boolean>>;

interface BooleanFlagConfiguration {
  variants: { on: true; off: false };
  defaultVariant: 'on' | 'off';
  disabled: boolean;
}

/**
 * Builds a `TypedInMemoryProvider`-compatible flag configuration covering the
 * whole registry. Flags not present in `values` evaluate to their registry
 * default. The shape is structurally compatible with both the server and web
 * OpenFeature SDKs' in-memory providers.
 */
export const buildInMemoryFlagConfiguration = (
  values: FeatureFlagValues = {}
): Record<FeatureFlagKey, BooleanFlagConfiguration> => {
  const entries = featureFlagKeys.map((key) => {
    const value = values[key] ?? featureFlagDefinitions[key].defaultValue;

    return [
      key,
      {
        variants: { on: true, off: false },
        defaultVariant: value ? 'on' : 'off',
        disabled: false,
      },
    ] as const;
  });

  return Object.fromEntries(entries) as Record<
    FeatureFlagKey,
    BooleanFlagConfiguration
  >; // Object.fromEntries widens keys to string; entries are built from featureFlagKeys so the Record is exhaustive
};
